import * as React from 'react'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Stack, Typography, useTheme } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../utils/tableFunctions'
import { ContractorBriefDto } from '../../services/api/sharedTypes/Contractor'
import TableButtonDelete from '../table/TableButtonDelete'
import { RouteConstants } from '../../navigation/navigation-types'
import TableButtonOpen from '../table/TableButtonOpen'
import EnhancedTableHead from '../table/EnhancedTableHead'
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import CustomTableCell from '../common/CustomTableCell'
import { getNumberRoundedInFormattedString } from '../../utils/global-utils'

const headCells: readonly HeadCell[] = [
    {
        id: 'name',
        numeric: false,
        disablePadding: false,
        label: 'Jméno',
    },
    {
        id: 'paidInTotal',
        numeric: true,
        disablePadding: false,
        label: 'Vyplaceno',
        component: <CreditCardRoundedIcon />,
        centered: true,
    },
    {
        id: 'eventCount',
        numeric: true,
        disablePadding: false,
        label: 'Události za období',
        component: <EventRoundedIcon />,
        centered: true,
    },
    {
        id: 'phoneCount',
        numeric: true,
        disablePadding: false,
        label: 'Tel.',
        component: <LocalPhoneRoundedIcon />,
        centered: true,
    },
    {
        id: 'emailCount',
        numeric: true,
        disablePadding: false,
        label: 'Email',
        component: <MailOutlineRoundedIcon />,
        centered: true,
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]
interface ContractTableProps {
    title: string
    contracts: ContractorBriefDto[]
    onDelete: (id: string) => void
}

const ContractorTable: React.FC<ContractTableProps> = ({
    title,
    contracts,
    onDelete,
}: ContractTableProps) => {
    const theme = useTheme()

    const [order, setOrder] = React.useState<Order>('asc')
    const [orderBy, setOrderBy] = React.useState<keyof ContractorBriefDto>('name')

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property as keyof ContractorBriefDto)
    }

    return (
        <>
            <Stack justifyContent='space-between' direction='row' mt={3} mb={3}>
                <Typography variant='h6' color='primary'>
                    {title}
                </Typography>
                <Typography mt={theme.spacing(1)} variant='caption'>
                    {contracts.length} záznamů
                </Typography>
            </Stack>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={contracts.length}
                            headCells={headCells}
                        />
                        <TableBody>
                            {stableSort(
                                contracts.map((row) => {
                                    return {
                                        ...row,
                                        canBeAssigned: row.canBeAssigned ? 1 : 0,
                                        emails: row.emails ? row.emails.join(', ') : '',
                                        phones: row.phones ? row.phones.join(', ') : '',
                                        createdAt: row.createdAt ? row.createdAt.toLocaleString() : '',
                                        lastModifiedAt: row.lastModifiedAt ? row.lastModifiedAt.toLocaleString() : '',
                                        assignedEvents: row.assignedEvents ? row.assignedEvents.length : 0,
                                        note: row.note || '',
                                    }
                                }),
                                getComparator(order, orderBy)
                            ).map((row, index: number) => {
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={index}
                                        style={{
                                            height: '42px',
                                        }}
                                    >
                                        <CustomTableCell
                                            isText
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.workersAndCarriers + '/' + row.id}
                                        >
                                            {row.name + ' ' + row.lastName}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.workersAndCarriers + '/' + row.id}
                                        >
                                            {getNumberRoundedInFormattedString(row.paidInTotal)}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.workersAndCarriers + '/' + row.id}
                                        >
                                            {row.eventCount}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.workersAndCarriers + '/' + row.id}
                                        >
                                            {row.phoneCount}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            center
                                            style={{ cursor: 'pointer' }}
                                            route={RouteConstants.workersAndCarriers + '/' + row.id}
                                        >
                                            {row.emailCount}
                                        </CustomTableCell>
                                        <CustomTableCell
                                            align='right'
                                            sx={{
                                                paddingRight: '14px',
                                            }}
                                        >
                                            <Stack spacing={theme.spacing(2)} direction='row' justifyContent='end'>
                                                <TableButtonOpen route={RouteConstants.workersAndCarriers} id={row.id} />
                                                <TableButtonDelete
                                                    nameOfEntityInDialogMessage={row.name + ' ' + row.lastName}
                                                    onClick={() => onDelete(row.id)}
                                                />
                                            </Stack>
                                        </CustomTableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </>
    )
}

export default ContractorTable
